/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DefaultButton, IButtonProps, IButtonStyles } from '@fluentui/react/lib/Button';
import { ContextualMenuItemType, IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import React from 'react';
import { defineMessages } from 'react-intl';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logFilter } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { DocumentsFilter, FileFilter, LabelFilter } from 'src/customText/appShell/dataTaggingPage/samplerStore';

export type DocumentFilterDropdownProps = {
    isExtractionProject: boolean;
    appliedFilters: DocumentsFilter;
    onChangeFilter: (filter: DocumentsFilter) => void;
    isDisabled?: boolean;
};

const messages = defineMessages({
    filter: {
        id: 'customText.documentFilterDropdown.filter',
        defaultMessage: 'Filter'
    },
    showDocuments: {
        id: 'customText.documentFilterDropdown.showDocuments',
        defaultMessage: 'Show documents'
    },
    unlabeledDocuments: {
        id: 'customText.documentFilterDropdown.unlabeledDocuments',
        defaultMessage: 'Unlabeled documents'
    },
    allLabeledDocuments: {
        id: 'customText.documentFilterDropdown.allLabeledDocuments',
        defaultMessage: 'All labeled documents'
    },
    allAutoLabeledDocuments: {
        id: 'customText.documentFilterDropdown.allAutoLabeledDocuments',
        defaultMessage: 'All auto-labeled documents'
    },
    trainingOnly: {
        id: 'customText.documentFilterDropdown.trainingOnly',
        defaultMessage: 'Training only'
    },
    testingOnly: {
        id: 'customText.documentFilterDropdown.testingOnly',
        defaultMessage: 'Testing only'
    },
    entities: {
        id: 'customText.documentFilterDropdown.entities',
        defaultMessage: 'Entities'
    },
    classes: {
        id: 'customText.documentFilterDropdown.classes',
        defaultMessage: 'Classes'
    }
});

const filesFilters: FileFilter[] = ['tagged', 'untagged', 'autotagged', 'testing', 'training'];

const buttonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    root: { border: 'none' },
    menuIcon: { display: 'none' },
    rootDisabled: { background: 'transparent' }
});

/**
 * A component rendering the filters available for documents.
 */
export const DocumentFilterDropdown = (props: DocumentFilterDropdownProps) => {
    const { isDisabled = false, appliedFilters, onChangeFilter, isExtractionProject } = props;

    const { rootStore } = useGlobalState();
    const { serviceName } = getPortalFromUrl();
    const { currentCustomTextProjectStore } = rootStore;
    const { projectKind } = currentCustomTextProjectStore;
    const { localize } = useLocalization();

    const isFilterApplied = appliedFilters.files.length || appliedFilters.labels.length;

    const isFilterChecked = (key: string | FileFilter) =>
        appliedFilters.files.includes(key as FileFilter) || Boolean(appliedFilters.labels.find(l => l.id === key));

    const onChangeFileFilter = (filter: FileFilter, checked: boolean) => {
        let { files } = appliedFilters;

        if (checked) {
            files = appliedFilters.files.filter(f => f !== filter);
        } else if (filesFilters.includes(filter)) {
            files = [...appliedFilters.files.filter(f => !filesFilters.includes(f)), filter];
        } else {
            files = [...appliedFilters.files, filter];
        }

        logFilter(serviceName, 'DataLabeling', filter);
        onChangeFilter({ ...appliedFilters, files });
    };

    const onChangeLabelFilter = (label: LabelFilter, checked: boolean) => {
        logFilter(serviceName, 'DataLabeling', 'Entity');
        onChangeFilter({
            ...appliedFilters,
            labels: checked ? appliedFilters.labels.filter(l => l.id !== label.id) : [...appliedFilters.labels, label]
        });
    };
    const items: IContextualMenuItem[] = [
        {
            key: 'files',
            itemType: ContextualMenuItemType.Header,
            text: localize(messages.showDocuments)
        },
        {
            key: 'untaggedFiles',
            text: localize(messages.unlabeledDocuments),
            canCheck: true,
            checked: isFilterChecked('untagged'),
            onClick: () => onChangeFileFilter('untagged', isFilterChecked('untagged'))
        },
        {
            key: 'allTaggedFiles',
            text: localize(messages.allLabeledDocuments),
            canCheck: true,
            checked: isFilterChecked('tagged'),
            onClick: () => onChangeFileFilter('tagged', isFilterChecked('tagged'))
        }
    ];

    if (isExtractionProject) {
        items.push({
            key: 'allAutoTaggedFiles',
            text: localize(messages.allAutoLabeledDocuments),
            canCheck: true,
            checked: isFilterChecked('autotagged'),
            onClick: () => onChangeFileFilter('autotagged', isFilterChecked('autotagged'))
        });
    }

    items.push(
        {
            key: 'trainingOnly',
            text: localize(messages.trainingOnly),
            checked: isFilterChecked('training'),
            onClick: () => onChangeFileFilter('training', isFilterChecked('training'))
        },
        {
            key: 'testingOnly',
            text: localize(messages.testingOnly),
            checked: isFilterChecked('testing'),
            onClick: () => onChangeFileFilter('testing', isFilterChecked('testing'))
        }
    );

    if (projectKind === 'CustomEntityRecognition' || projectKind === 'CustomHealthcare') {
        const extractionItems: IContextualMenuItem[] = [
            {
                key: 'entities',
                itemType: ContextualMenuItemType.Header,
                text: localize(messages.entities)
            },
            ...currentCustomTextProjectStore.extractionStore.entities.map(e => ({
                key: e.id,
                text: e.name,
                canCheck: true,
                checked: isFilterChecked(e.id),
                onClick: () => onChangeLabelFilter({ id: e.id, name: e.name }, isFilterChecked(e.id))
            }))
        ];

        items.push(...extractionItems);
    } else {
        const classificationItems: IContextualMenuItem[] = [
            {
                key: 'classes',
                itemType: ContextualMenuItemType.Header,
                text: localize(messages.classes)
            },
            ...currentCustomTextProjectStore.classificationStore.classes.map(c => ({
                key: c.id,
                text: c.name,
                canCheck: true,
                checked: isFilterChecked(c.id),
                onClick: () => onChangeLabelFilter({ id: c.id, name: c.name }, isFilterChecked(c.id))
            }))
        ];

        items.push(...classificationItems);
    }

    return (
        <DefaultButton
            iconProps={{ iconName: isFilterApplied ? 'FilterSolid' : 'Filter' }}
            menuProps={{
                shouldFocusOnMount: true,
                items
            }}
            text={localize(messages.filter)}
            styles={buttonStyles}
            disabled={isDisabled}
        />
    );
};
